import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import Image from 'next/image';
import CopyToClipboard from 'react-copy-to-clipboard';
import clsx from 'clsx';

import { openModal } from 'features/utilsSlice';
import { previewUrl } from 'utils/helpers';
import useProfileOwner from 'hooks/useProfileOwner';
import useIsAdvertiser from 'hooks/useIsAdvertiser';
import useIsLoggedIn from 'hooks/useIsLoggedIn';
import Toast from 'utils/toast';
import ShareIcon from 'public/icons/profile/share.svg';
import CloseIcon from 'public/icons/close-white.svg';
import CoverImage from './CoverImage';
import Gallery from './Gallery';
import FollowProfile from 'components/FollowProfile';
import IconMainContainer from 'components/IconMainContainer';
import styles from 'styles/components/profile/ProfileCover.module.scss';

const ProfileCover = ({ profile, setProfile, profilePreview, handleClose, isDialog }) => {
  const [url, setUrl] = useState('');
  const loggedIn = useIsLoggedIn();
  const isAdvertiser = useIsAdvertiser();
  const profileOwner = useProfileOwner(profile);
  const dispatch = useDispatch();
  const shouldOpenGallery =
    (!profileOwner && profile.photos?.length > 0) ||
    (!profile.adult && !loggedIn && profile.photos?.length > 0) ||
    profileOwner;

  const router = useRouter();
  const fromDiscoverPage = router.pathname.includes('/discover');

  const openGallery = () =>
    !profilePreview && !isDialog && shouldOpenGallery && dispatch(openModal({ name: 'Gallery' }));

  useEffect(() => {
    if (!fromDiscoverPage) return;

    const currentUrl = previewUrl(`/u/${profile?.username}`);

    setUrl(currentUrl);
  }, [profile?.username, fromDiscoverPage]);

  return (
    <>
      <div
        onClick={openGallery}
        className={clsx(styles.coverImg, {
          [styles.noHover]: profilePreview || profile?.photos.length === 0 || isDialog,
        })}
        style={
          profilePreview || isDialog
            ? {
                top: 0,
              }
            : {}
        }
      >
        <div
          className={clsx(styles.shadow, {
            [styles.purpleBackground]: profile.photos.length === 0,
          })}
        />

        <CoverImage photos={profile.photos} edit={profileOwner && !profilePreview} />

        <div className={styles.topActions}>
          <span></span>

          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              filter: 'drop-shadow(3px 5px 2px rgb(0 0 0 / 0.4))',
            }}
          >
            {fromDiscoverPage && (
              <CopyToClipboard text={url}>
                <ShareIcon
                  width={25}
                  height={25}
                  onClick={() => {
                    Toast('success', 'Copied profile link to clipboard!', {
                      icon: (
                        <IconMainContainer src="/icons/notifications/thumb-up.svg" alt="Thumb Up" />
                      ),
                    });
                  }}
                />
              </CopyToClipboard>
            )}

            {loggedIn && isAdvertiser && (
              <div style={{ marginLeft: 15 }}>
                <FollowProfile profile={profile} setProfile={setProfile} />
              </div>
            )}
          </div>
        </div>

        {isDialog && (
          <div className={styles.closeIcon} onClick={handleClose}>
            <CloseIcon width={15} height={15} alt="Close" className="hover-icon" />
          </div>
        )}
      </div>

      <div className={styles.cover}>
        <div className={styles.shadow} />

        <div className={styles.background}>
          {profile.avatar_url && (
            <Image layout="fill" src={profile.avatar_url} alt="Background" unoptimized />
          )}
        </div>
      </div>

      <Gallery profile={profile} setProfile={setProfile} edit={profileOwner && !profilePreview} />
    </>
  );
};

ProfileCover.propTypes = {
  profile: PropTypes.object.isRequired,
  setProfile: PropTypes.func.isRequired,
  profilePreview: PropTypes.bool,
  showCartIcon: PropTypes.bool,
};

ProfileCover.defaultProps = {
  profilePreview: false,
  showCartIcon: false,
};

export default ProfileCover;
